import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Box,
  Avatar,
  Typography,
  Chip,
  Link,
  TableSortLabel,
} from '@mui/material';
import { Person, ExpandMore, ExpandLess, VerifiedUser } from '@mui/icons-material';
import useSortTable from '../../../hooks/useSortTable';

const stripHtml = (html) => html?.replace(/<[^>]*>/g, '') || '';

const ThreadTable = ({ threads, readOnly }) => {
  const [expanded, setExpanded] = useState({});
  const { sorted, sortBy, sortDir, handleSort } = useSortTable(threads);

  const toggleExpanded = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const SortCell = ({ label, field }) => (
    <TableCell onClick={() => handleSort(field)} sx={{ cursor: 'pointer', userSelect: 'none' }}>
      <TableSortLabel active={sortBy === field} direction={sortBy === field ? sortDir : 'asc'}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <SortCell label="USUARIO" field="users_correspondence_threads_from_idTousers.name" />
              <TableCell>MENSAJE</TableCell>
              <TableCell>ETIQUETADOS</TableCell>
              <SortCell label="FECHA" field="createdAt" />
              <TableCell>FIRMA</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No hay hilos de conversación. Haz clic en "Crear Hilo" para agregar uno.
                </TableCell>
              </TableRow>
            ) : (
              sorted.map((thread) => {
                const fromUser = thread.users_correspondence_threads_from_idTousers;
                const taggedUsers = Array.isArray(thread.tagged_users_data)
                  ? thread.tagged_users_data
                  : [];
                const roleName = fromUser?.roleName || thread.user?.roleName;

                return (
                  <TableRow key={thread.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                          <Person fontSize="small" />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {fromUser?.name || thread.user?.name || 'Usuario'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {fromUser?.email || thread.user?.email || ''}
                          </Typography>
                          {roleName && (
                            <Box sx={{ mt: 0.25 }}>
                              <Chip label={roleName} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {expanded[thread.id] ? (
                        <Box>
                          <Box
                            sx={{ maxWidth: 500, '& *': { maxWidth: '100%' } }}
                            dangerouslySetInnerHTML={{ __html: thread.message }}
                          />
                          <Link
                            component="button"
                            variant="caption"
                            onClick={() => toggleExpanded(thread.id)}
                            sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}
                          >
                            <ExpandLess fontSize="small" /> Ver menos
                          </Link>
                        </Box>
                      ) : (
                        <Box>
                          <Typography variant="body2" sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {stripHtml(thread.message).slice(0, 100)}{stripHtml(thread.message).length > 100 ? '…' : ''}
                          </Typography>
                          {thread.message && (
                            <Link
                              component="button"
                              variant="caption"
                              onClick={() => toggleExpanded(thread.id)}
                              sx={{ display: 'flex', alignItems: 'center' }}
                            >
                              <ExpandMore fontSize="small" /> Ver mensaje
                            </Link>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {taggedUsers.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {taggedUsers.map((u) => (
                            <Chip
                              key={u.id}
                              label={`@${u.name}`}
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{ height: 18, fontSize: 10 }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(thread.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {thread.electronicSignature ? (
                        <Tooltip
                          title={`Firmado electrónicamente — Token: ${thread.electronicSignature.signatureToken} | Firmante: ${thread.electronicSignature.signerName} | Decreto 2364/2012`}
                          arrow
                        >
                          <Chip
                            icon={<VerifiedUser fontSize="small" />}
                            label="Firmado"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        </Tooltip>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ThreadTable;
